// FormPreview — the shared, type-switch field renderer for the forms surface.
//
// Extracted from FormsEmbed's inline control logic (project rule §51: the
// wizard PREVIEW and the live EMBED must render identically, so both consume
// this one component). Rendering is a switch over the canonical field-type
// ENUM (text/email/phone/textarea/select/checkbox/multiselect/number/date) —
// NOT the old enum/maxLength heuristic. A `note` field (static text, spec D2)
// renders a paragraph block via a dedicated branch BEFORE the control switch —
// it never renders a control — and any field may carry a `description` hint
// (spec D1) rendered below its control.
//
// Values are typed Record<string, string | string[] | boolean | number>:
//   text/email/phone/textarea/select/date -> string
//   checkbox                              -> boolean
//   multiselect                           -> string[]
//   number                                -> number
//   note                                  -> (no value — never rendered as a control)
//
// FormPreview self-styles via the Phase-0 embed primitives (EmbedField +
// EmbedInput/EmbedTextarea/EmbedSelect) and `useEmbedTokens()`. Callers MUST
// render it inside an <EmbedThemeProvider> (the embed wraps its whole tree;
// the portal Preview tab wraps each <FormPreview> site). Inline styles only —
// no Tailwind — so the embed bundle stays portal-free.

import { useEffect, type CSSProperties, type ReactElement } from "react";
import {
  EmbedField,
  EmbedInput,
  EmbedTextarea,
  EmbedSelect,
  useEmbedTokens,
} from "../../embed-shared/primitives";
import type { TokenSet } from "../../embed-shared/tokens";

// -- Types ------------------------------------------------------------------

export interface PreviewFieldOption {
  value: string;
  label: string;
}

export interface PreviewFieldSchema {
  name: string;
  title: string;
  // canonical ENUM incl. static-text "note"; "string" is a legacy alias of "text".
  type: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  format?: string;
  pattern?: string;
  // select / multiselect — canonical {value,label} options.
  options?: PreviewFieldOption[];
  // legacy snake/camel back-compat: a bare string[] enum renders as a select.
  enum?: string[];
  // number
  min?: number;
  max?: number;
  step?: number;
  // date (ISO yyyy-mm-dd)
  minDate?: string;
  maxDate?: string;
  // Static helper text rendered under the control (spec D1); on a note, below
  // the paragraph. Empty string = absent (T1 carry-over: render nothing).
  description?: string;
}

/** A single field value emitted by onChange / read from values. */
export type PreviewValue = string | string[] | boolean | number;

export interface FormPreviewProps {
  fields: PreviewFieldSchema[];
  values: Record<string, PreviewValue>;
  onChange: (name: string, value: PreviewValue) => void;
  /** Per-field error messages keyed by field name. */
  errors?: Record<string, string>;
  /** Disables all controls (e.g. while submitting). */
  disabled?: boolean;
  /** Prefix for control `id`s so multiple forms on a page stay unique. */
  idPrefix: string;
  /** Optional ref-collector so the embed can focus the first errored field. */
  setRef?: (name: string, el: HTMLElement | null) => void;
}

// -- Helpers ----------------------------------------------------------------

/** Normalize a field's selectable options from canonical or legacy shapes. */
function fieldOptions(field: PreviewFieldSchema): PreviewFieldOption[] {
  if (field.options && field.options.length > 0) return field.options;
  if (field.enum && field.enum.length > 0) {
    return field.enum.map((v) => ({ value: v, label: v }));
  }
  return [];
}

function asString(v: PreviewValue | undefined): string {
  if (v == null) return "";
  if (Array.isArray(v)) return "";
  if (typeof v === "boolean") return "";
  return String(v);
}

function asStringArray(v: PreviewValue | undefined): string[] {
  return Array.isArray(v) ? v : [];
}

function asBoolean(v: PreviewValue | undefined): boolean {
  return v === true;
}

// -- Renderer ----------------------------------------------------------------

export function FormPreview({
  fields,
  values,
  onChange,
  errors,
  disabled,
  idPrefix,
  setRef,
}: FormPreviewProps): ReactElement {
  const t = useEmbedTokens();

  // The embed's focus-first-error behavior needs the underlying DOM element per
  // field. The Phase-0 primitives are plain (non-forwardRef) function
  // components, so we resolve each control by its deterministic `id` after
  // render and report it through the same `setRef(name, el)` contract the embed
  // already wires — preserving focus-first-error byte-for-byte.
  useEffect(() => {
    if (!setRef) return;
    for (const field of fields) {
      const el = typeof document !== "undefined"
        ? document.getElementById(`${idPrefix}-${field.name}`)
        : null;
      setRef(field.name, el);
    }
    return () => {
      for (const field of fields) setRef(field.name, null);
    };
    // Re-resolve when the field set or prefix changes; `setRef` is a stable
    // collector from the embed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, idPrefix, setRef]);

  return (
    <>
      {fields.map((field) => {
        const id = `${idPrefix}-${field.name}`;
        const errId = `${id}-err`;
        const err = errors?.[field.name];
        const required = !!field.required;

        const args: ControlArgs = {
          field,
          id,
          errId,
          err,
          value: values[field.name],
          onChange,
          disabled: !!disabled,
          required,
          t,
        };

        // Static text (spec D2): a note is a readable paragraph block — never a
        // control, label, required marker, or focus target — and MUST be handled
        // BEFORE renderControl so the unknown->text-input default can't catch it.
        if (canonicalType(field) === "note") {
          return (
            <div key={field.name} data-testid="form-note" style={noteBlockStyle(t)}>
              <p style={noteTextStyle(t)}>{field.title}</p>
              {field.description ? (
                <p style={noteHintStyle(t)}>{field.description}</p>
              ) : null}
            </div>
          );
        }

        // The checkbox lays out control-before-label inline; every other type is
        // label-above-control via EmbedField (which owns the label + error copy).
        if (canonicalType(field) === "checkbox") {
          return (
            <div key={field.name} style={checkboxGroupStyle(t)}>
              <label htmlFor={id} style={checkboxLabelStyle(t)}>
                {renderCheckbox(args)}
                {required && (
                  <span style={{ color: t.colors.error }} aria-hidden="true">
                    *{" "}
                  </span>
                )}
                {field.title}
              </label>
              {err ? (
                <p id={errId} style={fieldErrorStyle(t)} role="alert">
                  {err}
                </p>
              ) : field.description ? (
                // Same slot contract as EmbedField: hint below the control+label
                // row (spec D1), hidden while an error shows — error wins. The
                // id must match ariaProps' `${id}-help` for the checkbox's own
                // aria-describedby to actually resolve (fix round 1, Important).
                <p id={`${id}-help`} style={checkboxHelpStyle(t)}>
                  {field.description}
                </p>
              ) : null}
            </div>
          );
        }

        return (
          <EmbedField
            key={field.name}
            label={field.title}
            htmlFor={id}
            required={required}
            error={err}
            help={field.description || undefined}
          >
            {renderControl(args)}
          </EmbedField>
        );
      })}
    </>
  );
}

// EmbedField owns the per-field error announcement: its error <span> carries
// id=`${htmlFor}-err` + role="alert", and each control sets
// aria-describedby=`${id}-err` (errId) with id===htmlFor, so the association
// resolves end-to-end. The checkbox path below lays out control-before-label
// inline, so it renders its OWN <p id={errId} role="alert"> (EmbedField is not
// used there) — exactly one error element per field carries the id+role.

function checkboxGroupStyle(t: TokenSet): CSSProperties {
  return { display: "flex", flexDirection: "column", gap: t.spacing.xs, marginBottom: t.spacing.md };
}

function checkboxLabelStyle(t: TokenSet): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: t.spacing.sm,
    fontSize: t.fontSize.sm,
    fontWeight: 500,
    color: t.colors.text,
  };
}

function fieldErrorStyle(t: TokenSet): CSSProperties {
  return { fontSize: t.fontSize.xs, color: t.colors.error, margin: 0 };
}

// Static note block (spec D2): one readable paragraph + optional hint, spaced
// like every other field (bottom margin) so lists of notes flow with inputs.
function noteBlockStyle(t: TokenSet): CSSProperties {
  return { marginBottom: t.spacing.md };
}

function noteTextStyle(t: TokenSet): CSSProperties {
  return {
    margin: 0,
    fontSize: t.fontSize.sm,
    lineHeight: t.lineHeight.normal,
    color: t.colors.text,
  };
}

function noteHintStyle(t: TokenSet): CSSProperties {
  return {
    margin: 0,
    marginTop: t.spacing.xs,
    fontSize: t.fontSize.xs,
    color: t.colors.textSecondary,
  };
}

// Checkbox hint (spec D1): the group div is a flex column with `gap`, so the
// hint needs no top margin — it mirrors EmbedField's help slot visually.
function checkboxHelpStyle(t: TokenSet): CSSProperties {
  return { margin: 0, fontSize: t.fontSize.xs, color: t.colors.textSecondary };
}

interface ControlArgs {
  field: PreviewFieldSchema;
  id: string;
  errId: string;
  err: string | undefined;
  value: PreviewValue | undefined;
  onChange: (name: string, value: PreviewValue) => void;
  disabled: boolean;
  required: boolean;
  t: TokenSet;
}

/** Canonical type, mapping "string" -> "text" and a bare enum -> select. */
function canonicalType(field: PreviewFieldSchema): string {
  // "note" is an explicit, authoritative type (spec D2) — never reinterpreted
  // by a stray `options`/`enum` array. The backend rejects options on a note
  // (Task 1 metaschema), but the portal's manual-edit Preview renders
  // unvalidated JSON, so this must hold even for hand-edited, invalid input.
  if (field.type === "note") return "note";
  const type = field.type === "string" ? "text" : field.type;
  const hasOptions = fieldOptions(field).length > 0;
  if ((type === "select" || hasOptions) && type !== "multiselect") return "select";
  return type;
}

function renderControl(args: ControlArgs): ReactElement {
  switch (canonicalType(args.field)) {
    case "select":
      return renderSelect(args);
    case "multiselect":
      return renderMultiselect(args);
    case "textarea":
      return renderTextarea(args);
    case "number":
      return renderNumber(args);
    case "date":
      return renderDate(args);
    case "email":
    case "phone":
    case "text":
    default:
      return renderTextInput(args, canonicalType(args.field));
  }
}

function ariaProps(args: ControlArgs) {
  return {
    "aria-required": args.required,
    "aria-invalid": !!args.err,
    // Error wins over the description hint — matches EmbedField's own slot
    // semantics (help is hidden while an error is shown), so a control never
    // announces a hint that's currently hidden.
    "aria-describedby": args.err
      ? args.errId
      : args.field.description
        ? `${args.id}-help`
        : undefined,
  };
}

function renderSelect(args: ControlArgs): ReactElement {
  const { field, id, value, onChange, disabled, required, err } = args;
  return (
    <EmbedSelect
      id={id}
      name={field.name}
      value={asString(value)}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={required}
      invalid={!!err}
      {...ariaProps(args)}
      disabled={disabled}
    >
      <option value="" disabled>
        Please select
      </option>
      {fieldOptions(field).map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </EmbedSelect>
  );
}

function renderMultiselect(args: ControlArgs): ReactElement {
  const { field, id, value, onChange, disabled, required, err } = args;
  const selected = asStringArray(value);
  return (
    <EmbedSelect
      id={id}
      name={field.name}
      multiple
      value={selected}
      onChange={(e) => {
        const next = Array.from(e.target.selectedOptions).map((o) => o.value);
        onChange(field.name, next);
      }}
      required={required}
      invalid={!!err}
      {...ariaProps(args)}
      disabled={disabled}
    >
      {fieldOptions(field).map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </EmbedSelect>
  );
}

function renderCheckbox(args: ControlArgs): ReactElement {
  const { field, id, value, onChange, disabled, required, t } = args;
  return (
    <input
      id={id}
      name={field.name}
      type="checkbox"
      checked={asBoolean(value)}
      onChange={(e) => onChange(field.name, e.target.checked)}
      required={required}
      {...ariaProps(args)}
      disabled={disabled}
      style={{ width: 16, height: 16, accentColor: t.colors.primary, flex: "none" }}
    />
  );
}

function renderTextarea(args: ControlArgs): ReactElement {
  const { field, id, value, onChange, disabled, required, err } = args;
  return (
    <EmbedTextarea
      id={id}
      name={field.name}
      value={asString(value)}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={required}
      invalid={!!err}
      {...ariaProps(args)}
      disabled={disabled}
      maxLength={field.maxLength}
      placeholder="Enter your message"
    />
  );
}

function renderNumber(args: ControlArgs): ReactElement {
  const { field, id, value, onChange, disabled, required, err } = args;
  return (
    <EmbedInput
      id={id}
      name={field.name}
      type="number"
      value={value == null || Array.isArray(value) || typeof value === "boolean" ? "" : String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        // Empty clears to "" (string) so a required-empty number still validates;
        // a parseable value emits a number.
        if (raw === "") {
          onChange(field.name, "");
          return;
        }
        const n = Number(raw);
        onChange(field.name, Number.isNaN(n) ? raw : n);
      }}
      required={required}
      invalid={!!err}
      {...ariaProps(args)}
      disabled={disabled}
      min={field.min}
      max={field.max}
      step={field.step}
      placeholder="Please enter"
    />
  );
}

function renderDate(args: ControlArgs): ReactElement {
  const { field, id, value, onChange, disabled, required, err } = args;
  return (
    <EmbedInput
      id={id}
      name={field.name}
      type="date"
      value={asString(value)}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={required}
      invalid={!!err}
      {...ariaProps(args)}
      disabled={disabled}
      min={field.minDate}
      max={field.maxDate}
    />
  );
}

function renderTextInput(args: ControlArgs, type: string): ReactElement {
  const { field, id, value, onChange, disabled, required, err } = args;
  // Map the canonical type (and legacy format/name hints) to the HTML input type.
  const inputType =
    type === "email" || field.format === "email"
      ? "email"
      : type === "phone" || field.name === "phone"
        ? "tel"
        : "text";
  return (
    <EmbedInput
      id={id}
      name={field.name}
      type={inputType}
      value={asString(value)}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={required}
      invalid={!!err}
      {...ariaProps(args)}
      disabled={disabled}
      maxLength={field.maxLength}
      placeholder="Please enter"
    />
  );
}
